import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { CATEGORY_META } from '../types';

const BUILTIN_CATEGORY_NAMES = new Set(
  [
    ...Object.keys(CATEGORY_META),
    ...Object.values(CATEGORY_META).map((m) => m.label),
    // Legacy/localized labels that might exist in DB rows.
    'cibo & ristorazione',
    'trasporti',
    'intrattenimento',
    'salute',
    'shopping',
    'bollette & utenze',
    'casa & affitto',
    'istruzione',
    'altro',
  ].map((v) => v.toLowerCase().trim()),
);

export interface CustomCategory {
  id: string;
  name: string;
  emoji: string;
  count?: number;
}

export function useCategories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CustomCategory[]>([]);

  const toTitleCase = (s: string): string => s
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

  const fetchCategories = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, emoji, count')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    
    if (!error && data) {
      const dedupe = new Map<string, CustomCategory>();

      data.forEach((c) => {
        const normalizedRaw = c.name.toLowerCase().trim();
        if (BUILTIN_CATEGORY_NAMES.has(normalizedRaw)) {
          return;
        }

        const title = toTitleCase(c.name);
        const dedupeKey = title.toLowerCase();
        if (!dedupe.has(dedupeKey)) {
          dedupe.set(dedupeKey, {
            ...c,
            name: title,
          });
        }
      });

      setCategories(Array.from(dedupe.values()));
    }
  }, [user]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`categories:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'categories', filter: `user_id=eq.${user.id}` },
        () => fetchCategories(),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, fetchCategories]);

  return { categories, refetch: fetchCategories };
}
